import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getHeaderStickyMenuShown } from '@yme/components/Header/redux/selectors';
import { useWindowSize } from '@yme/hooks/index';
import { HeroSectionYoutube } from '@yme/molecules/hero-section-youtube';
import { FOR_OWNERS_COMPONENT_ID } from '@yme/organisms/for-owners/constants';
import { HeroSectionContent } from '@yme/pdp-components/HeroSection/HeroSectionContent/HeroSectionContent';
import { getCommonLabels, getRTL } from '@yme/selectors/common';
import { PRODUCT_PHOTO_DISCLAIMER_KEY, PRODUCT_SERVICE_CODES, PRODUCT_SHORT_INTRO, } from '@yme/selectors/constants';
import { getAttribute, getIsLoadedProductDetails, getProductBlockStrategy, getProductDetails, } from '@yme/selectors/products';
import { getAttributeByName, getImageSrc, scrollToId, selectColorTheme, toStringArray, } from '@yme/utils/common';
import { ImageSuffix } from '@yme/utils/constants';
import { PRODUCT_CARD_SECTION_ID } from '../ProductCard/constants';
import { getHeroYoutubeId } from '../ProductVideo/selectors';
import { HeroSectionContentSkeleton } from './HeroSectionContent/HeroSectionContentSkeleton';
import { HeroSectionTheme, HeroSectionThemeSkeleton } from './HeroSectionTheme';
import { selectProductCardHeroImage } from './selectors';
import { HeroBannerStyled, HeroImageStyled } from './styled';
export default function HeroSection(props) {
    const { labels, configuratorVersion, configuratorLeadFormPagePath = '', isInEditor } = props;
    const productCardHeroImage = useSelector(selectProductCardHeroImage);
    const youtubeId = useSelector(getHeroYoutubeId);
    const isRightToLeft = useSelector(getRTL);
    const productPhotoDisclaimer = useSelector(getAttribute(PRODUCT_PHOTO_DISCLAIMER_KEY));
    const productBlockStrategy = useSelector(getProductBlockStrategy);
    const productShortIntro = useSelector(getAttribute(PRODUCT_SHORT_INTRO));
    const isLoadedProductDetails = useSelector(getIsLoadedProductDetails);
    const commonLabels = useSelector(getCommonLabels);
    const headerStickyMenuShown = useSelector(getHeaderStickyMenuShown);
    const productDetails = useSelector(getProductDetails);
    const productPhotoDisclaimers = toStringArray(productPhotoDisclaimer);
    const { isMobileView, isInfiniteDesktopView } = useWindowSize();
    const isVideoShowed = isInfiniteDesktopView && youtubeId;
    const currentColorTheme = productBlockStrategy
        ? selectColorTheme(productBlockStrategy?.value)
        : null;
    const amountOfHeightMenu = isMobileView ? 64 : 80;
    const yOffset = !headerStickyMenuShown ? amountOfHeightMenu * 2 : amountOfHeightMenu;
    const hasForOwnersComponent = Boolean(document.getElementById(FOR_OWNERS_COMPONENT_ID));
    const onScrollToIdHandler = useCallback(() => {
        scrollToId({
            id: hasForOwnersComponent ? FOR_OWNERS_COMPONENT_ID : PRODUCT_CARD_SECTION_ID,
            yOffset,
        });
    }, [hasForOwnersComponent, yOffset]);
    const productServiceCodes = productDetails?.data?.variants
        ?.map((variant) => {
        return getAttributeByName(variant.attributes, PRODUCT_SERVICE_CODES) || [];
    })
        .filter((value) => value.length > 0)
        .reduce((acc, val) => {
        val.forEach((item) => {
            if (!acc.includes(item)) {
                acc.push(item);
            }
        });
        return acc;
    }, []) ?? [];
    const imageUrl = isMobileView
        ? getImageSrc(productCardHeroImage, ImageSuffix.Mobile)
        : productCardHeroImage;
    if (isInEditor && !productCardHeroImage) {
        return null;
    }
    return (React.createElement(HeroBannerStyled, { isRightToLeft: isRightToLeft },
        React.createElement(HeroImageStyled, { imageUrl: !isVideoShowed ? imageUrl : '' },
            isVideoShowed && (React.createElement(HeroSectionYoutube, { videoId: youtubeId, preview: imageUrl })),
            isInEditor || isLoadedProductDetails ? (React.createElement(HeroSectionContent, { productPhotoDisclaimers: productPhotoDisclaimers, currentColorTheme: currentColorTheme, configuratorVersion: configuratorVersion, configuratorLeadFormPagePath: `${configuratorLeadFormPagePath}?modelName=${encodeURIComponent(productDetails?.data?.name ?? '')}&productServiceCodes=${encodeURIComponent(productServiceCodes.join(','))}&slug=${productDetails.data?.slug ?? ''}`, labels: {
                    ...labels,
                    common_from_label: commonLabels.common_from_label,
                }, isRightToLeft: isRightToLeft, isMobileView: isMobileView, isInfiniteDesktopView: isInfiniteDesktopView, hasForOwnersComponent: hasForOwnersComponent, scrollToId: onScrollToIdHandler })) : (React.createElement(HeroSectionContentSkeleton, null))),
        isMobileView && isLoadedProductDetails && (React.createElement(HeroSectionTheme, { currentColorTheme: currentColorTheme, productShortIntro: productShortIntro, testRideLabel: labels.pdpHeroSection_testRide_button, productPhotoDisclaimers: productPhotoDisclaimers, fromLabel: commonLabels.common_from_label, configuratorLeadFormPagePath: `${configuratorLeadFormPagePath}?modelName=${encodeURIComponent(productDetails?.data?.name ?? '')}&productServiceCodes=${encodeURIComponent(productServiceCodes.join(','))}&slug=${productDetails.data?.slug ?? ''}`, scrollToId: onScrollToIdHandler })),
        isMobileView && !isLoadedProductDetails && !isInEditor && React.createElement(HeroSectionThemeSkeleton, null)));
}
HeroSection.displayName = 'HeroSection_AEMComponent';
